import { useCallback, useEffect, useRef } from 'react';
import isFunction from 'lodash/isFunction';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import type { DownloadToastPollingState } from 'Components/DownloadCSV/DownloadCSV.types';
import { PollingState } from 'Components/DownloadCSV/DownloadCSV.types';
import { logger } from 'Services/logger/logger';

import { getDownloadToastProps } from './useDownloadCsvToast.helpers';

const downloadToastDismissTimeout = 400;

const displayDownloadToast = (state: DownloadToastPollingState) => {
	const props = getDownloadToastProps(state);
	return toast(props);
};

export const useDownloadCsvToast = (state: PollingState): void => {
	const dismissCallbackRef = useRef<() => void>();

	const resetToast = useCallback(async () => {
		if (isFunction(dismissCallbackRef?.current)) {
			dismissCallbackRef?.current?.();
			dismissCallbackRef.current = undefined;

			await new Promise((resolve) => {
				setTimeout(resolve, downloadToastDismissTimeout);
			});
		}
	}, []);

	const toastHandler = useCallback(async () => {
		if (state === PollingState.Inactive) {
			return;
		}

		if (state === PollingState.Polling) {
			dismissCallbackRef.current = dismissCallbackRef?.current ?? displayDownloadToast(state);
			return;
		}

		await resetToast();

		if (state === PollingState.Success) {
			return;
		}

		displayDownloadToast(state);
	}, [resetToast, state]);

	useEffect(() => {
		toastHandler().catch((error) => {
			logger.error({ error, message: 'Error displaying download toast', scope: 'useDownloadCsvToast' });
		});
	}, [resetToast, state, toastHandler]);
};
